import React, { useContext, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Slide,
    Fade,
    Grow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ModalContext } from "../../context/modalContext";

// Animation transitions
const SlideTransition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const FadeTransition = forwardRef(function Transition(props, ref) {
    return <Fade ref={ref} {...props} />;
});

const GrowTransition = forwardRef(function Transition(props, ref) {
    return <Grow ref={ref} {...props} />;
});

const getTransitionComponent = (animation) => {
    switch (animation) {
        case "slide":
            return SlideTransition;
        case "grow":
        case "fade-scale":
            return GrowTransition;
        case "fade":
        default:
            return FadeTransition;
    }
};

const getMaxWidth = (modalSize) => {
    switch (modalSize) {
        case "modal-sm":
            return "sm";
        case "modal-md":
            return "md";
        case "modal-lg":
            return "lg";
        case "modal-xl":
            return "xl";
        default:
            return "sm";
    }
};

/**
 * Enterprise-grade Custom Modal Component with Material UI
 * Wrapper around MUI Dialog with Bootstrap-like API for backward compatibility
 */
const CustomModal = forwardRef(
    (
        {
            children,
            modalName = "customModal",
            modalSize = "modal-sm",
            modalCustomClass = "",
            animation = "fade-scale",
            open: controlledOpen,
            onClose: controlledOnClose,
            title,
            showCloseButton = true,
            fullWidth = true,
            fullScreen = false,
            disableBackdropClick = false,
            sx,
            ...rest
        },
        ref,
    ) => {
        const { isModalOpen, closeModal, openModal } = useContext(ModalContext);

        // Support both controlled and uncontrolled modes
        const isControlled = controlledOpen !== undefined;
        const modalOpen = isControlled ? controlledOpen : isModalOpen(modalName);

        const handleClose = (event, reason) => {
            // Prevent closing on backdrop click only if explicitly disabled
            if (
                disableBackdropClick &&
                (reason === "backdropClick" || reason === "escapeKeyDown")
            ) {
                return;
            }

            if (isControlled && controlledOnClose) {
                controlledOnClose();
            } else {
                closeModal(modalName);
            }
        };

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            open: () => openModal(modalName),
            close: () => closeModal(modalName),
        }));

        const TransitionComponent = getTransitionComponent(animation);
        const maxWidth = getMaxWidth(modalSize);

        // Parse children to extract title, content, and actions
        // If title is provided as prop, use structured layout
        // Otherwise, render children as-is for custom layouts
        let dialogTitle = title;
        let dialogContent = children;
        let dialogActions = null;
        let useCustomLayout = false;

        if (children && Array.isArray(children)) {
            const modalHeader = children.find((child) =>
                child?.props?.className?.includes("modal-header"),
            );
            const modalBody = children.find((child) =>
                child?.props?.className?.includes("modal-body"),
            );
            const modalFooter = children.find((child) =>
                child?.props?.className?.includes("modal-footer"),
            );

            // If we have modal-header/body/footer structure but no title prop, use custom layout
            if (modalHeader && !title) {
                useCustomLayout = true;
            } else if (modalHeader && title) {
                // If we have both structure and title prop, extract sections
                dialogTitle = modalHeader.props.children;
                if (modalBody) {
                    dialogContent = modalBody.props.children;
                }
                if (modalFooter) {
                    dialogActions = modalFooter.props.children;
                }
            }
        }

        return (
            <Dialog
                open={modalOpen}
                onClose={handleClose}
                TransitionComponent={TransitionComponent}
                maxWidth={maxWidth}
                fullWidth={fullWidth}
                fullScreen={fullScreen}
                className={modalCustomClass}
                disableEscapeKeyDown={false}
                keepMounted={false}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                        },
                    },
                }}
                sx={{
                    "& .MuiDialog-paper": {
                        borderRadius: "12px",
                        boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
                        margin: { xs: "16px", sm: "32px" },
                        maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
                        width: "100%",
                    },
                    "& .MuiDialogContent-root": {
                        overflowY: "auto",
                    },
                    // Consistent styling for legacy Bootstrap-style modal structure
                    "& .modal-header": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 24px",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                        "& h5, & .modal-title": {
                            margin: 0,
                            fontWeight: 600,
                            fontSize: "1.125rem",
                        },
                    },
                    "& .modal-body": {
                        padding: "24px",
                        overflowY: "auto",
                    },
                    "& .modal-footer": {
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "8px",
                        padding: "12px 24px",
                        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                    },
                    ...sx,
                }}
                data-modal-name={modalName}
                {...rest}
            >
                {useCustomLayout ? (
                    // Render custom layout as-is
                    <>{children}</>
                ) : (
                    // Use structured Material UI layout
                    <>
                        {dialogTitle && (
                            <DialogTitle
                                sx={{
                                    m: 0,
                                    p: "16px 24px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                                }}
                            >
                                <span style={{ flex: 1, fontWeight: 600 }}>{dialogTitle}</span>
                                {showCloseButton && (
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => closeModal(modalName)}
                                        sx={{
                                            color: (theme) => theme.palette.grey[500],
                                            "&:hover": {
                                                backgroundColor: (theme) => theme.palette.grey[100],
                                            },
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                )}
                            </DialogTitle>
                        )}
                        {dialogContent && (
                            <DialogContent dividers sx={{ p: "24px" }}>
                                {dialogContent}
                            </DialogContent>
                        )}
                        {dialogActions && (
                            <DialogActions
                                sx={{
                                    p: "12px 24px",
                                    gap: 1,
                                    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                                }}
                            >
                                {dialogActions}
                            </DialogActions>
                        )}
                    </>
                )}
            </Dialog>
        );
    },
);

CustomModal.propTypes = {
    modalName: PropTypes.string,
    modalSize: PropTypes.string,
    modalCustomClass: PropTypes.string,
    animation: PropTypes.string,
    children: PropTypes.node,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    title: PropTypes.node,
    showCloseButton: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fullScreen: PropTypes.bool,
    disableBackdropClick: PropTypes.bool,
    sx: PropTypes.object,
};

CustomModal.displayName = "CustomModal";

export default CustomModal;
